import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import AuthModal from './AuthModal';

const Navbar = () => {
  const { user } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-lg border-b border-gray-800" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Cosmic Cat
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="hover:text-purple-400 transition-colors" data-testid="nav-home">Home</Link>
              <Link to="/shop" className="hover:text-purple-400 transition-colors" data-testid="nav-shop">Shop</Link>
              
              <button 
                onClick={() => navigate('/cart')}
                className="relative hover:text-purple-400 transition-colors"
                data-testid="nav-cart"
              >
                <ShoppingCart className="w-6 h-6" />
                {getItemCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                    {getItemCount()}
                  </span>
                )}
              </button>

              {user ? (
                <button 
                  onClick={() => navigate('/account')}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 hover:bg-purple-500/30 transition-all"
                  data-testid="nav-account"
                >
                  <User className="w-4 h-4" />
                  {user.name}
                </button>
              ) : (
                <button 
                  onClick={() => setShowAuth(true)}
                  className="btn-primary"
                  data-testid="nav-login"
                >
                  Login
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2" data-testid="mobile-menu">
              <Link 
                to="/" 
                className="block px-4 py-2 hover:bg-gray-800 rounded"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/shop" 
                className="block px-4 py-2 hover:bg-gray-800 rounded"
                onClick={() => setMobileMenuOpen(false)}
              >
                Shop
              </Link>
              <button 
                onClick={() => {
                  navigate('/cart');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-gray-800 rounded flex items-center justify-between"
              >
                Cart
                {getItemCount() > 0 && (
                  <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full">
                    {getItemCount()}
                  </span>
                )}
              </button>
              {user ? (
                <button 
                  onClick={() => {
                    navigate('/account');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-800 rounded"
                >
                  Account
                </button>
              ) : (
                <button 
                  onClick={() => {
                    setShowAuth(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-800 rounded"
                >
                  Login
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
};

export default Navbar;
